import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import BASE_URL from '../../config';

// Visually hides content while keeping it in the accessibility tree --
// standard "sr-only" clip technique, used for the live-region status
// announcement below (screen-reader users hear it, sighted users don't
// need to see it since the message already visibly disappears).
const srOnlyStyles = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const ChatPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [selectedDeviceSerial, setSelectedDeviceSerial] = useState('');

  // Delete-message state: confirmation dialog + in-flight/announcement state.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  // Announced via the sr-only aria-live region below -- lets screen-reader
  // users hear the result of a delete even though the message itself has
  // already silently disappeared from the (also-live) message log.
  const [statusMessage, setStatusMessage] = useState('');

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const token = localStorage.getItem('jwt_token');

useEffect(() => {
  const fetchDevices = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDevices(response.data);

      // Automatically select first device if only one device is returned
      if (response.data.length === 1) {
        setSelectedUid(response.data[0].uid);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  fetchDevices();
}, [token]);


  useEffect(() => {
    if (!selectedUid) return;

    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await axios.post(
          `${BASE_URL}/api/get_cust_messages/`,
          { uid: selectedUid },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setMessages(response.data);

        const device = devices.find(d => d.uid === selectedUid);
        setSelectedDeviceSerial(device ? device.serial : '');
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedUid, devices]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behaviour: 'smooth',
      });
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 100);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(
        `${BASE_URL}/api/create_text_portal/`,
        {
          sender: 'PORTAL',
          recipient: selectedUid,
          message: newMessage,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNewMessage('');

      const response = await axios.post(
        `${BASE_URL}/api/get_cust_messages/`,
        { uid: selectedUid },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const openDeleteDialog = (msg) => {
    setMessageToDelete(msg);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingInProgress) return; // don't let Escape/backdrop-click cut off an in-flight delete
    setDeleteDialogOpen(false);
    setMessageToDelete(null);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    setDeletingInProgress(true);
    try {
      await axios.post(
        `${BASE_URL}/api/delete_cust_message/`,
        { id: messageToDelete.id },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessages((prev) => prev.filter((m) => m.id !== messageToDelete.id));
      setStatusMessage('Message deleted.');
    } catch (error) {
      console.error('Error deleting message:', error);
      setStatusMessage('Could not delete the message. Please try again.');
    } finally {
      setDeletingInProgress(false);
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleString([], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  return (
    <Box
      maxWidth="600px"
      mx="auto"
      mt={4}
      p={3}
      borderRadius={2}
      component={Paper}
      sx={{
        background: 'rgba(35, 47, 62, 0.6)',
        border: '1px solid rgba(255, 153, 0, 0.2)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        '@media (max-width: 768px)': {
          mx: 0,
          p: 1.5,
          boxSizing: 'border-box',
          width: '100%',
        },
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#ffffff', textAlign: 'center' }}>
        Device Chat
      </Typography>

      {/* Announces delete outcomes to screen readers. role="status" + aria-live="polite"
          is enough on its own -- assertive would interrupt the user mid-task for a
          non-urgent confirmation, which is more annoying than helpful here. */}
      <Box role="status" aria-live="polite" aria-atomic="true" sx={srOnlyStyles}>
        {statusMessage}
      </Box>

      {loadingDevices ? (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress sx={{ color: '#ff9900' }} />
        </Box>
      ) : (
        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#aab7c4' }}>Select a device</InputLabel>
          <Select
            value={selectedUid}
            onChange={(e) => setSelectedUid(e.target.value)}
            label="Select a device"
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 153, 0, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 153, 0, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 153, 0, 0.6)',
              },
              '& .MuiSvgIcon-root': {
                color: '#ffffff',
              },
              background: 'rgba(35, 47, 62, 0.8)',
            }}
          >
            {devices.map((device) => (
              <MenuItem key={device.uid} value={device.uid}>
                {device.serial}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedUid && (
        <>
          <Paper
            variant="outlined"
            sx={{
              height: 400,
              overflowY: 'auto',
              p: 2,
              my: 2,
              background: 'rgba(15, 20, 25, 0.8)',
              border: '1px solid rgba(255, 153, 0, 0.2)',
              borderRadius: '8px',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
            onScroll={handleScroll}
            ref={chatContainerRef}
            role="log"
            aria-live="polite"
            aria-relevant="additions removals"
            aria-label={`Chat messages with ${selectedDeviceSerial || 'device'}`}
          >
            {loadingMessages ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress sx={{ color: '#ff9900' }} />
              </Box>
            ) : (
              messages.map((msg) => {
                const isDeviceSender = msg.sender === selectedUid;
                const whoLabel = isDeviceSender ? selectedDeviceSerial || 'Device' : 'You';
                const truncatedMessage =
                  msg.message.length > 60 ? `${msg.message.slice(0, 60)}...` : msg.message;
                return (
                  <Box
                    key={msg.id}
                    display="flex"
                    flexDirection="column"
                    alignItems={isDeviceSender ? 'flex-start' : 'flex-end'}
                    mb={1}
                  >
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.5, color: '#aab7c4' }}
                    >
                      {whoLabel}
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection={isDeviceSender ? 'row' : 'row-reverse'}
                      alignItems="center"
                      gap={0.5}
                    >
                      <Box
  px={2}
  py={1.5}
  sx={{
    background: isDeviceSender
      ? 'rgba(35, 47, 62, 0.8)'
      : 'linear-gradient(135deg, #ff9900 0%, #ff6600 100%)',
    color: 'white',
    border: isDeviceSender ? '1px solid rgba(255, 153, 0, 0.2)' : 'none',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  }}
  borderRadius={2}
  maxWidth="70%"
>
  <Typography variant="body2" sx={{ color: 'white' }}>{msg.message}</Typography>
  <Typography
    variant="caption"
    sx={{
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.8)',
      textAlign: 'right',
      mt: 0.5,
      display: 'block',
    }}
  >
    {formatTime(msg.timestamp)}
  </Typography>
</Box>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(msg)}
                        aria-label={`Delete message from ${whoLabel} at ${formatTime(msg.timestamp)}: ${truncatedMessage}`}
                        sx={{
                          color: 'rgba(170, 183, 196, 0.7)',
                          '&:hover': { color: '#f44336', background: 'rgba(244, 67, 54, 0.1)' },
                          '&:focus-visible': {
                            outline: '2px solid #ff9900',
                            outlineOffset: '2px',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
            <div ref={chatEndRef} />
          </Paper>

          {!isAtBottom && (
            <Box display="flex" justifyContent="center" my={1}>
              <Button
                size="small"
                onClick={scrollToBottom}
                sx={{
                  background: 'rgba(255, 153, 0, 0.2)',
                  color: '#ff9900',
                  border: '1px solid rgba(255, 153, 0, 0.4)',
                  '&:hover': {
                    background: 'rgba(255, 153, 0, 0.3)',
                  }
                }}
              >
                Scroll to Latest
              </Button>
            </Box>
          )}

          <Box
            display="flex"
            gap={2}
            sx={{
              '@media (max-width: 768px)': {
                gap: 0.75,
                width: '100%',
              },
            }}
          >
            <TextField
              fullWidth
              placeholder="Type a message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              variant="outlined"
              inputProps={{ maxLength: 1024 }}
              helperText={`${newMessage.length}/1024`}
              FormHelperTextProps={{ sx: { color: newMessage.length >= 1024 ? '#f44336' : 'rgba(170, 183, 196, 0.6)', textAlign: 'right', mr: 0 } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  background: 'rgba(35, 47, 62, 0.8)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 153, 0, 0.3)',
                  borderRadius: '6px',
                },
                '& .MuiInputBase-input': {
                  color: '#ffffff',
                  padding: '10px 14px',
                  fontSize: '1rem',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(170, 183, 196, 0.6)',
                  opacity: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiInputBase-root:hover': {
                  borderColor: 'rgba(255, 153, 0, 0.5)',
                },
                '& .MuiInputBase-root.Mui-focused': {
                  borderColor: 'rgba(255, 153, 0, 0.6)',
                  boxShadow: '0 0 8px rgba(255, 153, 0, 0.3)',
                },
                '@media (max-width: 768px)': {
                  '& .MuiInputBase-input': {
                    padding: '8px 10px',
                    fontSize: '0.875rem',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              sx={{
                background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important',
                color: 'white !important',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
                flexShrink: '0 !important',
                whiteSpace: 'nowrap !important',
                minWidth: 'auto !important',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0069d9 0%, #004085 100%) !important',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 123, 255, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(35, 47, 62, 0.5) !important',
                  color: 'rgba(170, 183, 196, 0.5) !important',
                },
                '@media (max-width: 768px)': {
                  padding: '0.5rem 0.75rem !important',
                  fontSize: '0.8rem !important',
                  minWidth: '60px !important',
                  width: '60px !important',
                },
              }}
            >
              Send
            </Button>
          </Box>
        </>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-message-dialog-title"
        aria-describedby="delete-message-dialog-description"
      >
        <DialogTitle id="delete-message-dialog-title">Delete this message?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-message-dialog-description">
            {messageToDelete
              ? `This will permanently delete: "${messageToDelete.message}". This cannot be undone.`
              : 'This will permanently delete the message. This cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Cancel gets autoFocus, not Delete -- a destructive confirm dialog
              shouldn't let a stray Enter key press delete something. */}
          <Button onClick={closeDeleteDialog} disabled={deletingInProgress} autoFocus>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteMessage}
            color="error"
            disabled={deletingInProgress}
          >
            {deletingInProgress ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatPage;
